import React from "react";
import { Link } from "react-router-dom";
import { useApiQuery } from "@/lib/useApiQuery";
import { AlertOctagon, ArrowUpRight, Wrench } from "lucide-react";

function hours(v) {
  if (v == null) return "—";
  if (v >= 24) return `${(v / 24).toFixed(1)} day`;
  if (v >= 1) return `${v.toFixed(1)} hr`;
  return `${Math.round(v * 60)} min`;
}

/** Current-week fault snapshot for the Overview page. */
export function FaultsSummary() {
  const { data, err } = useApiQuery(["faultsSummary"], "/faults/summary");

  return (
    <div className="mt-12" data-testid="overview-faults-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#212121] flex items-center gap-2">
            <Wrench className="h-4 w-4 text-[#156F5B]" /> Faults this week
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">Rule violations detected across your portfolio.</p>
        </div>
        <Link to="/faults" className="k-btn-outline" data-testid="overview-faults-link">
          View all faults <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="mt-4 k-card overflow-hidden">
        {err ? (
          <div className="p-6 text-sm text-red-600">{err}</div>
        ) : !data ? (
          <div className="p-8 text-center k-sublabel">Loading faults…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280]" data-testid="overview-faults-empty">
            No faults detected this week.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 divide-x divide-[#F0F2F1] border-b border-[#F0F2F1]">
              <div className="p-4">
                <div className="k-sublabel">Faults</div>
                <div className="mt-1 text-xl font-heading font-semibold text-[#212121]" data-testid="overview-faults-count">
                  {data.fault_count}
                </div>
              </div>
              <div className="p-4">
                <div className="k-sublabel">Critical</div>
                <div className="mt-1 text-xl font-heading font-semibold" style={{ color: data.critical_count ? "#B91C1C" : "#212121" }}>
                  {data.critical_count}
                </div>
              </div>
              <div className="p-4">
                <div className="k-sublabel">Impact</div>
                <div className="mt-1 text-xl font-heading font-semibold text-[#212121]">
                  ${Number(data.total_cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
            {data.rows.map((r, i) => (
              <Link
                to="/faults"
                key={`${r.site_id}-${r.rule_id}`}
                className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#F0F2F1] last:border-0 hover:bg-[#F9FAFB]"
                style={{ transition: "background-color .2s ease" }}
                data-testid={`overview-fault-row-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-heading font-semibold text-[#212121] flex items-center gap-2">
                    {r.critical && <AlertOctagon className="h-3.5 w-3.5 text-[#B91C1C] shrink-0" />}
                    <span className="truncate">{r.rule}</span>
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5 truncate">
                    {r.site_dis} · {(r.equips || []).slice(0, 2).join(", ") || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-heading font-semibold text-[#212121]">{hours(r.hours)}</div>
                  {r.cost ? (
                    <div className="text-[11px] text-[#C2410C]">
                      ${r.cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                  ) : null}
                </div>
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
